import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { LineChart } from "echarts/charts";
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
    DataZoomComponent,
    MarkPointComponent
} from "echarts/components";
import * as echarts from "echarts/core";
import { rgbaColor } from "../../../../../helpers/utils";
import { useAppContext } from "../../../../../providers/AppProvider";
import ReactEchart from "../../../../../components/common/ReactEchart";
import { isNaN } from "underscore";

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LineChart,
    LegendComponent,
    DataZoomComponent,
    MarkPointComponent
]);

const tooltipFormatter = (params) => {
    let tooltip = `<div>${params[0].name}</div>`;
    tooltip += params
        .map(
            ({ value, borderColor, seriesName }) =>
                `   
        <div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
        <span class='text-600'>${seriesName}: ${!isNaN(value) ? parseFloat(value).toFixed(2) : 0}°C</span>
        `
        )
        .join("<br/>");
    return tooltip;
};

const getOptions = (
    getThemeColor,
    allTemperatures,
    allTimestamps,
    isSingleDay
) => ({
    color: getThemeColor("gray-100"),
    dataZoom: [
        {
            type: 'slider', // Deslizador visible
            start: 0,
            end: 100,
            xAxisIndex: [0]
        },
        {
            type: 'inside', // Zoom con rueda del ratón y arrastre
            start: 0,
            end: 100,
            xAxisIndex: [0]
        }
    ],
    tooltip: {
        trigger: "axis",
        padding: [7, 10],
        backgroundColor: getThemeColor("gray-100"),
        borderColor: getThemeColor("gray-100"),
        textStyle: { color: getThemeColor("gray-1100") },
        borderWidth: 1,
        formatter: tooltipFormatter,
        transitionDuration: 0,
    },
    legend: {
        data: ["Temperatura"],
        show: false,
    },
    xAxis: {
        type: "category",
        data: allTimestamps,
        boundaryGap: false,
        axisPointer: {
            lineStyle: {
                color: getThemeColor("gray-300"),
                type: "dashed",
            },
        },
        splitLine: { show: false },
        axisLine: {
            lineStyle: {
                color: rgbaColor("#000", 0.01),
                type: "dashed",
            },
        },
        axisTick: { show: false },
        axisLabel: {
            color: getThemeColor("gray-400"),
            margin: 15,
            rotate: allTimestamps.length > 24 ? 45 : 0,
            formatter: (value) => {
                return dayjs(value).format("HH:mm");
            },

        },
    },
    yAxis: {
        type: "value",
        axisPointer: { show: false },
        splitLine: {
            lineStyle: {
                color: getThemeColor("gray-300"),
                type: "dashed",
            },
        },
        boundaryGap: false,
        axisLabel: {
            show: true,
            color: getThemeColor("gray-400"),
            margin: 15,
            formatter: '{value} °C'
        },
        axisTick: { show: false },
        axisLine: { show: false },
    },
    series: [
        {
            name: "Temperatura",
            type: "line",
            data: allTemperatures,
            lineStyle: { color: getThemeColor("primary") },
            itemStyle: {
                borderColor: getThemeColor("primary"),
                borderWidth: 2,
                backgroundColor: "transparent",
            },
            symbol: "circle",
            symbolSize: 10,
            emphasis: {
                scale: true,
            },
            markPoint: {
                symbolSize: 65,
                itemStyle: {
                    color: getThemeColor("primary")
                },
                label: {
                    show: true,
                    formatter: '{c}°C',
                    color: getThemeColor("white"),
                    fontSize: 11,
                },
                data: [
                    {
                        name: 'Máxima',
                        type: 'max',
                        itemStyle: { color: '#FF5733' },

                    },
                    {
                        name: 'Mínima',
                        type: 'min',
                        itemStyle: { color: '#3498DB' },

                    }
                ]
            },
            areaStyle: {
                color: {
                    type: "linear",
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                        {
                            offset: 0,
                            color: rgbaColor(getThemeColor("primary"), 0.2),
                        },
                        {
                            offset: 1,
                            color: rgbaColor(getThemeColor("primary"), 0),
                        },
                    ],
                },
            },
        },
    ],
    grid: { right: "38px", left: "45px", bottom: "25%", top: "13%" },
});

const TemperaturaChart = forwardRef(
    ({ data, isSingleDay = false }, ref) => {
        console.log(data);
        const { getThemeColor } = useAppContext();

        const allTemperatures = [];
        const allTimestamps = [];

        Object.values(data).forEach(dayOrHourEntries => {
            dayOrHourEntries.forEach(entry => {
                if (entry.temperatura !== undefined && !isNaN(entry.temperatura)) {
                    allTemperatures.push(entry.temperatura);
                    allTimestamps.push(entry.fecha);
                }
            });
        });

        return (
            <ReactEchart
                ref={ref}
                echarts={echarts}
                style={{ width: '100%', height: '95%' }}
                option={getOptions(
                    getThemeColor,
                    allTemperatures,
                    allTimestamps,
                    isSingleDay
                )}
            />
        );
    }
);

TemperaturaChart.propTypes = {
    data: PropTypes.object.isRequired,
    isSingleDay: PropTypes.bool
};

export default TemperaturaChart;